import {
  BriefApplicationResponseDto,
  BriefApplicationScreeningAnswerDto,
  BriefResponseDto,
  ScreeningExpectedAnswer,
  ScreeningQuestionTypeEnum,
} from '../../../../common/general-types/briefs.dto';

// ─── Public types ────────────────────────────────────────────────────────────

export interface ScreeningQaRow {
  questionId?: string;
  question:    string;
  type:        ScreeningQuestionTypeEnum | undefined;
  answer:      string;
  expected?:   ScreeningExpectedAnswer;
}

export type Verdict          = 'pass' | 'fail' | 'review';
export type ApplicantVerdict = 'pass' | 'partial' | 'fail' | 'review';

export interface EvaluatedRow extends ScreeningQaRow {
  verdict: Verdict;
}

export interface ApplicantEvaluation {
  results: EvaluatedRow[];
  passed:  number;
  failed:  number;
  reviews: number;
  total:   number;
  verdict: ApplicantVerdict;
}

// ─── Evaluation logic (single source of truth) ───────────────────────────────

export function evaluateAnswer(
  expected: ScreeningExpectedAnswer | undefined,
  answer: string,
): Verdict {
  if (!answer || answer.trim() === '') return 'review';
  if (!expected || expected.kind === 'manual') return 'review';

  if (expected.kind === 'exact') {
    return answer.trim().toLowerCase() === expected.value.toLowerCase()
      ? 'pass' : 'fail';
  }
  if (expected.kind === 'number') {
    const n = Number(answer);
    if (!Number.isFinite(n)) return 'fail';
    if (expected.operator === 'eq') return n === expected.value ? 'pass' : 'fail';
    if (expected.operator === 'gt') return n > expected.value  ? 'pass' : 'fail';
    if (expected.operator === 'lt') return n < expected.value  ? 'pass' : 'fail';
  }
  return 'review';
}

export function evaluateApplicant(
  brief: BriefResponseDto | null | undefined,
  applicant: BriefApplicationResponseDto | null | undefined,
): ApplicantEvaluation {
  const rows = buildScreeningQaRows(brief, applicant);
  const results: EvaluatedRow[] = rows.map((row) => ({
    ...row,
    verdict: evaluateAnswer(row.expected, row.answer),
  }));

  const passed  = results.filter((r) => r.verdict === 'pass').length;
  const failed  = results.filter((r) => r.verdict === 'fail').length;
  const reviews = results.filter((r) => r.verdict === 'review').length;
  const total   = results.length;

  let verdict: ApplicantVerdict;
  if (failed === 0 && passed === 0)       verdict = 'review';
  else if (failed === 0)                  verdict = 'pass';
  else if (passed === 0 && reviews === 0) verdict = 'fail';
  else                                    verdict = 'partial';

  return { results, passed, failed, reviews, total, verdict };
}

// ─── Row building (id-keyed with index fallback) ─────────────────────────────

function normalizeAnswerEntry(
  entry: string | BriefApplicationScreeningAnswerDto | undefined,
): { questionId?: string; question?: string; type?: ScreeningQuestionTypeEnum; answer: string } {
  if (entry == null)         return { answer: '' };
  if (typeof entry === 'string') return { answer: entry };
  return {
    questionId: entry.questionId,
    question:   entry.question,
    type:       entry.type,
    answer:     entry.answer ?? '',
  };
}

/** Zip brief screening questions with applicant.screeningAnswers for display. */
export function buildScreeningQaRows(
  brief: BriefResponseDto | null | undefined,
  applicant: BriefApplicationResponseDto | null | undefined,
): ScreeningQaRow[] {
  const qs  = brief?.screeningQuestions ?? [];
  const ans = (applicant?.screeningAnswers ?? []) as (BriefApplicationScreeningAnswerDto | string)[];

  // Build an id→answer map for new-style applications (questionId present)
  const answerById = new Map<string, BriefApplicationScreeningAnswerDto | string>();
  for (const a of ans) {
    if (typeof a !== 'string' && a.questionId) {
      answerById.set(a.questionId, a);
    }
  }

  const n = Math.max(qs.length, ans.length);
  const rows: ScreeningQaRow[] = [];

  for (let i = 0; i < n; i++) {
    const qDef      = qs[i];
    // Prefer id-based lookup; fall back to index
    const rawAnswer = qDef?.id && answerById.has(qDef.id)
      ? answerById.get(qDef.id)!
      : ans[i];
    const normalized = normalizeAnswerEntry(rawAnswer);

    rows.push({
      questionId: qDef?.id,
      question:   normalized.question?.trim() || qDef?.question?.trim() || '',
      type:       normalized.type ?? qDef?.type,
      answer:     (normalized.answer ?? '').trim(),
      expected:   qDef?.expected,
    });
  }
  return rows;
}

// ─── Legacy helpers (still used by existing code) ────────────────────────────

export function screeningHasAnyFilledAnswer(
  brief: BriefResponseDto | null | undefined,
  applicant: BriefApplicationResponseDto | null | undefined,
): boolean {
  return buildScreeningQaRows(brief, applicant).some((r) => r.answer.length > 0);
}

export function screeningFirstAnswerPreview(
  brief: BriefResponseDto | null | undefined,
  applicant: BriefApplicationResponseDto | null | undefined,
  maxLen = 56,
): string {
  const first = buildScreeningQaRows(brief, applicant).find((r) => r.answer.length > 0);
  if (!first) return '';
  const t = first.answer;
  return t.length > maxLen ? `${t.slice(0, maxLen - 1)}…` : t;
}
